import React, { useState } from 'react';
import { Play, Pause, Volume2, Download, Share2, Maximize } from 'lucide-react';

interface VideoPlayerProps {
  videoUrl: string;
  originalDuration: number;
  summaryDuration: number;
  compressionRatio: number;
  metadata: {
    title: string;
    channel: string;
    views: string;
    publishDate: string;
  };
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoUrl,
  originalDuration,
  summaryDuration,
  compressionRatio,
  metadata,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(100);

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatCompressionRatio = (ratio: number) => {
    return `${Math.round((1 - ratio) * 100)}% shorter`;
  };

  const handleDownload = () => {
    // Mock download functionality
    console.log('Downloading video...');
  };

  const handleShare = () => {
    // Mock share functionality
    console.log('Sharing video...');
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      {/* Video Container */}
      <div className="relative bg-gray-900 aspect-video">
        {/* Mock Video Player */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center mb-4 mx-auto">
              <Play className="w-12 h-12 text-white ml-1" />
            </div>
            <p className="text-lg font-medium opacity-75">AI-Generated Summary Video</p>
            <p className="text-sm opacity-50">Click to play</p>
          </div>
        </div>

        {/* Video Controls Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              {isPlaying ? (
                <Pause className="w-5 h-5 text-white" />
              ) : (
                <Play className="w-5 h-5 text-white ml-0.5" />
              )}
            </button>

            {/* Progress Bar */}
            <div className="flex-1">
              <div className="w-full bg-white/20 rounded-full h-1">
                <div
                  className="bg-white h-1 rounded-full transition-all duration-300"
                  style={{ width: `${(currentTime / summaryDuration) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Time Display */}
            <span className="text-white text-sm font-medium">
              {formatDuration(currentTime)} / {formatDuration(summaryDuration)}
            </span>

            {/* Volume Control */}
            <div className="flex items-center space-x-2">
              <Volume2 className="w-5 h-5 text-white" />
              <div className="w-16 bg-white/20 rounded-full h-1">
                <div
                  className="bg-white h-1 rounded-full"
                  style={{ width: `${volume}%` }}
                ></div>
              </div>
            </div>

            {/* Fullscreen */}
            <button className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded transition-colors">
              <Maximize className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Video Info */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
              {metadata.title} (AI Summary)
            </h3>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span>{metadata.channel}</span>
              <span>•</span>
              <span>{metadata.views} views</span>
              <span>•</span>
              <span>{metadata.publishDate}</span>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-blue-600 mb-1">
              {formatDuration(originalDuration)}
            </div>
            <div className="text-sm text-blue-700">Original Length</div>
          </div>
          <div className="bg-green-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-green-600 mb-1">
              {formatDuration(summaryDuration)}
            </div>
            <div className="text-sm text-green-700">Summary Length</div>
          </div>
          <div className="bg-orange-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-orange-600 mb-1">
              {formatCompressionRatio(compressionRatio)}
            </div>
            <div className="text-sm text-orange-700">Time Saved</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={handleDownload}
            className="flex-1 bg-gradient-to-r from-blue-500 to-green-500 text-white py-3 px-4 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2"
          >
            <Download className="w-5 h-5" />
            <span>Download Video</span>
          </button>
          <button
            onClick={handleShare}
            className="bg-gray-100 text-gray-700 py-3 px-4 rounded-xl font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center"
          >
            <Share2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};